import React from "react";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import { useMediaQuery } from "@mui/material";
import { AccountTypeEnum } from "@bn/api/apiv1";
import { useTranslation } from "react-i18next";
import { MandatorTypography } from "@components/MandatorTypography";
import AccountPreviewIcon from "./AccountPreviewIcon";

const AccountMetaContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  flex-grow: 1;
`;

const BankAvatarWrapper = styled.div`
  display: flex;
  margin-right: ${({ theme }) => theme.spacing(2)};
  ${({ theme }) => theme.breakpoints.up("sm")} {
    margin-right: ${({ theme }) => theme.spacing(4)};
  }
`;

const AccountNameContainer = styled.div`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AccountName = styled(MandatorTypography)`
  width: 100%;
`;

const AccountSubtitle = styled(MandatorTypography)`
  width: 100%;
`;

export interface AccountIconAndNameProps {
  isReference?: boolean;
  bankLogoURL: string;
  accountType: AccountTypeEnum;
  accountName: string;
  bankName?: string;
  bankCode?: string;
}

export const AccountIconAndName = ({
  isReference,
  bankLogoURL,
  accountType,
  accountName,
  bankName,
  bankCode,
}: AccountIconAndNameProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const iconSize = 40;

  return (
    <AccountMetaContainer>
      <BankAvatarWrapper>
        <AccountPreviewIcon
          isReference={isReference ?? false}
          iconUrl={bankLogoURL}
          type={accountType}
          size={iconSize}
        />
      </BankAvatarWrapper>
      <AccountNameContainer>
        <AccountName
          color={theme.palette.text.primary}
          variant="subtitle2"
          noWrap
          data-testid="account-name"
        >
          {accountName}
        </AccountName>
        <AccountSubtitle
          variant={isMobile ? "body4" : "body2"}
          lineHeight={isMobile ? "20px" : "24px"}
          color="textSecondary"
          noWrap
        >
          {bankName ?? t("accountView.noBank")}
          {bankCode && ` • ${bankCode}`}
        </AccountSubtitle>
      </AccountNameContainer>
    </AccountMetaContainer>
  );
};
